/**
 * Iter-238 — Admin Creator Bonus Manager
 *
 * Embedded into ClientInsightsRollup. Lets the client/admin:
 *   - Create new bonus tiers (label, metric, threshold, bonus amount)
 *   - List active tiers, archive/edit
 *   - Approve / mark-paid / cancel pending earnings
 *   - One-click "Recompute" to materialise earnings from current data
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Trophy, Plus, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

const money = (cents) =>
  cents == null ? '—'
    : `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const METRIC_LABELS = {
  conversions: 'Conversions',
  revenue_cents: 'Revenue ($)',
  orders: 'Orders',
  clicks: 'Clicks',
};

export default function BonusManager({ clientId }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: '', metric: 'conversions', threshold: 10, bonus_dollars: 100,
  });

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/creator-bonuses/overview?client_id=${clientId}`);
      setOverview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bonuses');
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const createTier = useCallback(async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    setBusy(true);
    try {
      const bonus_cents =
        form.metric === 'revenue_cents'
          ? Math.round(form.bonus_dollars * 100)
          : Math.round(form.bonus_dollars * 100);
      const threshold =
        form.metric === 'revenue_cents'
          ? Math.round(form.threshold * 100)   // user entered $ for the threshold
          : Math.round(form.threshold);
      await api.post('/admin/creator-bonuses/tiers', {
        client_id: clientId,
        label: form.label.trim(),
        metric: form.metric,
        threshold,
        bonus_cents,
      });
      toast.success('Bonus tier created');
      setShowForm(false);
      setForm({ label: '', metric: 'conversions', threshold: 10, bonus_dollars: 100 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  }, [form, clientId, load]);

  const recompute = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/creator-bonuses/recompute', { client_id: clientId });
      toast.success(`${r.data.new_earnings} new earning(s) materialised`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  }, [clientId, load]);

  const action = useCallback(async (earningId, kind, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/creator-bonuses/earnings/${earningId}/${kind}`, extra);
      toast.success(`Earning ${kind.replace('-', ' ')}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  }, [load]);

  const archiveTier = useCallback(async (tierId) => {
    if (!window.confirm('Archive this tier? Existing earnings stay.')) return;
    try {
      await api.patch(`/admin/creator-bonuses/tiers/${tierId}`, { status: 'archived' });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [load]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading bonus manager…
      </div>
    );
  }

  const summary = overview?.summary || {};
  const tiers   = overview?.tiers   || [];
  const earnings = overview?.earnings || [];

  return (
    <section className="mt-8" data-testid="bonus-manager">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900">Creator Pay-for-Performance</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={recompute} disabled={busy}
            data-testid="bonus-recompute-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Recompute
          </Button>
          <Button size="sm" onClick={() => setShowForm((s) => !s)}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="new-tier-btn">
            <Plus className="w-4 h-4 mr-1" /> New Tier
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Tile label="Active Tiers" value={summary.tiers_active || 0} testid="bm-tiers-active" />
        <Tile label="Pending Approval" value={money(summary.earnings_pending)} testid="bm-pending" />
        <Tile label="Approved" value={money(summary.earnings_approved)} testid="bm-approved" />
        <Tile label="Paid Out" value={money(summary.earnings_paid)} testid="bm-paid" />
      </div>

      {/* Create-tier form */}
      {showForm && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4"
          data-testid="bonus-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <Input placeholder="Bonus label (e.g. 'Top 10 Conversions')"
              value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              data-testid="tier-label-input" />
            <select value={form.metric}
              onChange={(e) => setForm({ ...form, metric: e.target.value })}
              className="border border-slate-200 rounded px-2 py-1.5 text-sm"
              data-testid="tier-metric-select">
              {Object.entries(METRIC_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <Input type="number" min="1"
              placeholder={form.metric === 'revenue_cents' ? 'Threshold ($)' : 'Threshold (count)'}
              value={form.threshold}
              onChange={(e) => setForm({ ...form, threshold: parseInt(e.target.value || '0', 10) })}
              data-testid="tier-threshold-input" />
            <Input type="number" min="1"
              placeholder="Bonus ($)"
              value={form.bonus_dollars}
              onChange={(e) => setForm({ ...form, bonus_dollars: parseFloat(e.target.value || '0') })}
              data-testid="tier-bonus-input" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={createTier} disabled={busy}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="create-tier-btn">
              Create Tier
            </Button>
          </div>
        </div>
      )}

      {/* Active tiers */}
      {tiers.length === 0 && !showForm && (
        <p className="text-sm text-slate-500 mb-4">
          No bonus tiers yet. Add a tier (e.g. "5 conversions → $100") to reward creators who hit performance milestones.
        </p>
      )}
      {tiers.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4"
          data-testid="bonus-tiers-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Label</th>
                <th className="text-left px-3 py-2">Metric</th>
                <th className="text-right px-3 py-2">Threshold</th>
                <th className="text-right px-3 py-2">Bonus</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t, i) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`tier-row-${i}`}>
                  <td className="px-4 py-2 font-medium text-slate-900">{t.label}</td>
                  <td className="px-3 py-2">{METRIC_LABELS[t.metric] || t.metric}</td>
                  <td className="px-3 py-2 text-right">
                    {t.metric === 'revenue_cents' ? money(t.threshold) : t.threshold}
                  </td>
                  <td className="px-3 py-2 text-right font-bold">{money(t.bonus_cents)}</td>
                  <td className="px-3 py-2">
                    <Badge variant={t.status === 'active' ? 'default' : 'outline'}>
                      {t.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {t.status === 'active' && (
                      <button onClick={() => archiveTier(t.id)}
                        className="text-xs text-rose-600 hover:underline"
                        data-testid={`archive-tier-${i}`}>
                        Archive
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Earnings lifecycle queue */}
      {earnings.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden"
          data-testid="bonus-earnings-queue">
          <div className="px-4 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-600">
            Earnings — pending approval → approved → paid
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Creator</th>
                <th className="text-left px-3 py-2">Tier</th>
                <th className="text-right px-3 py-2">Bonus</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {earnings.map((e, i) => (
                <tr key={e.id} className="border-t border-slate-100" data-testid={`earning-row-${i}`}>
                  <td className="px-4 py-2 font-medium text-slate-900">{e.creator_name}</td>
                  <td className="px-3 py-2">{e.label}</td>
                  <td className="px-3 py-2 text-right font-bold">{money(e.bonus_cents)}</td>
                  <td className="px-3 py-2">
                    <Badge className={
                      e.status === 'paid' ? 'bg-emerald-100 text-emerald-900'
                        : e.status === 'approved' ? 'bg-cyan-100 text-cyan-900'
                        : e.status === 'cancelled' ? 'bg-slate-100 text-slate-500'
                        : 'bg-amber-100 text-amber-900'
                    }>{e.status.toUpperCase()}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    {e.status === 'pending' && (
                      <Button size="sm" variant="outline" disabled={busy}
                        onClick={() => action(e.id, 'approve')}
                        data-testid={`approve-${i}`}>
                        Approve
                      </Button>
                    )}
                    {(e.status === 'approved' || e.status === 'pending') && (
                      <Button size="sm" disabled={busy}
                        onClick={() => {
                          const ref = window.prompt('Payout reference (Stripe ID, check #, etc):');
                          if (ref !== null) action(e.id, 'mark-paid', { payout_ref: ref });
                        }}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid={`mark-paid-${i}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Paid
                      </Button>
                    )}
                    {(e.status === 'pending' || e.status === 'approved') && (
                      <button onClick={() => {
                          const reason = window.prompt('Reason for cancelling?') || 'cancelled';
                          action(e.id, 'cancel', { reason });
                        }}
                        className="text-xs text-rose-600 hover:underline"
                        data-testid={`cancel-${i}`}>
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
